import { Alert, Grid } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { Button, FormControl, Snackbar } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../../LoadingScreen';
import { GeneralInfoContainer } from './GeneralInfoContainer';
import SaveDialog from './SaveDialog';
import { createCurrency, getCurrency, updateCurrency } from '../../../api/elton-payments/currency';
import { useNavigate } from 'react-router-dom';

const StyledButton = styled(Button)(({ theme }) => ({
  marginLeft: '1rem',
  height: '3rem',
  [theme.breakpoints.down('md')]: {
    margin: '2px',
    width: '100%'
  }
}));

export function Edit({ disabled, id, setId }: any) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [saveDialog, setSaveDialog] = useState<boolean>(false);
  const [currency, setCurrency] = useState<any>({
    currency: ''
  });
  const [message, setMessage] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    show: false,
    message: '',
    type: 'success'
  });

  const [changedCurrencyFields, setChangedCurrencyFields] = useState<any[]>([]);

  const fetchCurrency = useCallback(
    async (newId: string) => {
      if (newId) {
        setLoading(true);
        const result = await getCurrency(newId);
        setCurrency(result?.data.data);
        setChangedCurrencyFields([]);
        setLoading(false);
      } else {
        setLoading(false);
      }
    },
    [setCurrency, setLoading]
  );

  /**
   * Fetch mew offer on id change
   */
  useEffect(() => {
    fetchCurrency(id);
  }, [fetchCurrency, id]);

  /**
   * Handle input change, and update changedFields
   * @param value
   * @param field
   * @param displayName
   * @param displayValue
   */
  const handleChange = useCallback(
    (value: any, field: string, displayName: string = '', displayValue: any = '') => {
      const change = { value, field, displayName, displayValue };
      setChangedCurrencyFields([...changedCurrencyFields.filter((l) => l.field !== field), change]);
    },
    [changedCurrencyFields, setChangedCurrencyFields]
  );

  const handleChangeMultiple = useCallback(
    (currency: any) => {
      const changes = Object.keys(currency).map((key) => ({ field: key, value: currency[key] }));
      setChangedCurrencyFields(changedCurrencyFields.concat(changes));
    },
    [changedCurrencyFields, setChangedCurrencyFields]
  );

  /**
   * Reset state
   */
  const handleDiscard = () => {
    setCurrency({ ...currency });
    setChangedCurrencyFields([]);
  };

  /**
   * Save changedFields
   */
  const handleSave = async () => {
    // Close dialog
    setSaveDialog(false);

    // Change format to object
    const update: any = {};
    for (const change of changedCurrencyFields) {
      update[change.field] = change.value?.length === 0 ? 'null' : change.value; //Hacky solution for setting null value.
    }

    try {
      if (id) {
        await updateCurrency(currency.primary_id, update);
      } else {
        const result = await createCurrency(update);
        if (result.data.status == 'SUCCESS' && result.data.data.primary_id) {
          setId(result.data.data.primary_id);
          navigate(`/dashboard/currencies/edit/${result.data.data.primary_id}`);
        }
      }
      // Reset view
      setChangedCurrencyFields([]);

      // Show Alert
      setMessage({ show: true, message: 'Currency updated!', type: 'success' });

      // Fetch new offer
      await fetchCurrency(id);
    } catch (error: any) {
      let message = 'Unknown Error';
      if (error?.status_message) {
        message = error.status_message;
      } else if (error?.message) {
        message = error?.message;
      }
      setMessage({ show: true, message, type: 'error' });
    }
  };

  return loading ? (
    <div style={{ height: 'calc(100vh - 320px)' }}>
      <LoadingScreen sx={{}} />
    </div>
  ) : (
    <div>
      <FormControl style={{ width: '100%' }}>
        <Grid container spacing={3}>
          {/* Search field to find offer */}
          <Grid item xs={12} md={4} />

          {/* Fake row to force new line */}
          <Grid style={{ display: 'flex', justifyContent: 'flex-end' }} item xs={12} md={8}>
            <StyledButton
              disabled={!(changedCurrencyFields.length > 0)}
              variant="outlined"
              color="inherit"
              onClick={handleDiscard}
            >
              Discard changes
            </StyledButton>
            <StyledButton
              disabled={!(changedCurrencyFields.length > 0)}
              variant="outlined"
              color="primary"
              onClick={() => {
                setSaveDialog(true);
              }}
            >
              Save changes
            </StyledButton>
          </Grid>

          {/* Container with general information */}
          <Grid item xs={12}>
            <GeneralInfoContainer
              disabled={disabled}
              currency={currency}
              onChange={handleChange}
              onChangeMany={handleChangeMultiple}
            />
          </Grid>
        </Grid>
      </FormControl>

      {/* Save confirmation dialog */}
      <SaveDialog
        open={saveDialog}
        handleClose={() => setSaveDialog(false)}
        onSave={handleSave}
        changes={changedCurrencyFields}
      />

      <Snackbar
        open={message.show}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={() => setMessage({ ...message, show: false })}
      >
        <Alert
          onClose={() => setMessage({ ...message, show: false })}
          variant="outlined"
          severity={message.type}
          sx={{ width: '100%', backgroundColor: 'white' }}
        >
          {message.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
